import type { Dayjs } from 'dayjs';
import type { TFunction } from 'i18next';
import type {
    IpptAssessmentResultDataFragment,
    TenantUserReportDataFragment,
    TraineeReportDataFragment,
    TrainersNotesAssessmentResultDataFragment,
} from '../Trainee/TraineeReports.api';
import type {
    Profile,
    Performance,
    TraineeReportModel,
    SimpleMetric,
    PhysiologicalResult,
    PhysiologicalFitness,
    CognitiveFitness,
    ChartData,
    PsychologicalResult,
    ChartSeries,
    RadarDatum,
    RadarDataLeadership,
} from './model';

import type { Program } from '@/apolloClient.types';
import { convertShortLabel } from '@/components/blocks/AssessmentResults/shared/utils';
import type { Ml360AssessmentResultDataFragment } from '@/pages/AssessmentLauncher/ML360/ml360.api';
import type { SartAssessmentResultDataFragment } from '@/pages/AssessmentLauncher/SART/Sart.api';
import type { SelfDeterminationAssessmentResultDataFragment } from '@/pages/AssessmentLauncher/SelfDetermintation/SelfDetermination.api';
import type { SpatialPlanningAssessmentResultDataFragment } from '@/pages/AssessmentLauncher/SpatialPlanning/SpatialPlanning.api';
import type { TeamResilienceAssessmentResultDataFragment } from '@/pages/AssessmentLauncher/TeamResilience/TeamResilience.api';
import type { VisualReactionAssessmentResultDataFragment } from '@/pages/AssessmentLauncher/VisualReaction/VisualReaction.api';
import { DefaultTeamResilienceQuestionCategory } from '@/pages/AssessmentManagementPage/shared/ConfigurationSchema/teamResilienceFormSchema';
import type { BmpAssessmentResultDataFragment } from '@/pages/PhysicalResultPage/BMP/Create/CreateBmpAssessmentResult.api';
import type { CardioRespAssessmentResultDataFragment } from '@/pages/PhysicalResultPage/CardioResp/Create/CreateCardioRespAssessmentResult.api';
import type { FinSwimAssessmentResultDataFragment } from '@/pages/PhysicalResultPage/FinSwim/Create/CreateFinSwimAssessmentResult.api';
import type { March20kmAssessmentResultDataFragment } from '@/pages/PhysicalResultPage/March20km/Create/CreateMarch20kmResult.api';
import type { SocAssessmentResultDataFragment } from '@/pages/PhysicalResultPage/Soc/Create/CreateSocResult.api';
import type { StrengthAssessmentResultDataFragment } from '@/pages/PhysicalResultPage/Strength/Create/CreateStrengthAssessmentResult.api';
import type { WeightAssessmentResultDataFragment } from '@/pages/PhysicalResultPage/Weight/Create/CreateWeightResult.api';
import { formatSecondsAsMinSec, formatSecondsAsHoursMin } from '@/pages/PhysicalResultPage/shared/utils';

export const DefaultScores = {
    Ml360MaxScore: 7,
    SelfDeterminationMaxScore: 5,
    TeamResilienceMaxScore: 100,
    DefaultAverageScore: 0,
};

export const MetricLabels = {
    RunTime: '2.4km Run Time (mm:ss)',
};

const createDefaultMl360Data = (t: TFunction) => ({
    surveys: [],
    categoryAverages: [
        {
            category: t('traineeReports:ml360.awareness'),
            averageScore: DefaultScores.DefaultAverageScore,
            maximumPossibleScore: DefaultScores.Ml360MaxScore,
        },
        {
            category: t('traineeReports:ml360.calmAndPatience'),
            averageScore: DefaultScores.DefaultAverageScore,
            maximumPossibleScore: DefaultScores.Ml360MaxScore,
        },
        {
            category: t('traineeReports:ml360.openMind'),
            averageScore: DefaultScores.DefaultAverageScore,
            maximumPossibleScore: DefaultScores.Ml360MaxScore,
        },
        {
            category: t('traineeReports:ml360.focusAndPresence'),
            averageScore: DefaultScores.DefaultAverageScore,
            maximumPossibleScore: DefaultScores.Ml360MaxScore,
        },
        {
            category: t('traineeReports:ml360.growth'),
            averageScore: DefaultScores.DefaultAverageScore,
            maximumPossibleScore: DefaultScores.Ml360MaxScore,
        },
    ],
});

const createDefaultSelfDeterminationData = (t: TFunction) => ({
    surveys: [],
    categoryAverages: [
        {
            category: t('traineeReports:selfDetermination.autonomy'),
            averageScore: DefaultScores.DefaultAverageScore,
            maximumPossibleScore: DefaultScores.SelfDeterminationMaxScore,
        },
        {
            category: t('traineeReports:selfDetermination.competence'),
            averageScore: DefaultScores.DefaultAverageScore,
            maximumPossibleScore: DefaultScores.SelfDeterminationMaxScore,
        },
        {
            category: t('traineeReports:selfDetermination.relatedness'),
            averageScore: DefaultScores.DefaultAverageScore,
            maximumPossibleScore: DefaultScores.SelfDeterminationMaxScore,
        },
        {
            category: t('traineeReports:selfDetermination.overall'),
            averageScore: DefaultScores.DefaultAverageScore,
            maximumPossibleScore: DefaultScores.SelfDeterminationMaxScore,
        },
    ],
});

const createDefaultTeamResilienceData = (t: TFunction) => ({
    surveys: [],
    categoryAverages: [],
    subcategoryAverages: [
        {
            subcategory: t('traineeReports:teamResilience.physical'),
            category: t('traineeReports:teamResilience.teamResources'),
            averageScore: DefaultScores.DefaultAverageScore,
            maximumPossibleScore: DefaultScores.TeamResilienceMaxScore,
        },
        {
            subcategory: t('traineeReports:teamResilience.affective'),
            category: t('traineeReports:teamResilience.teamResources'),
            averageScore: DefaultScores.DefaultAverageScore,
            maximumPossibleScore: DefaultScores.TeamResilienceMaxScore,
        },
        {
            subcategory: t('traineeReports:teamResilience.cognitive'),
            category: t('traineeReports:teamResilience.teamResources'),
            averageScore: DefaultScores.DefaultAverageScore,
            maximumPossibleScore: DefaultScores.TeamResilienceMaxScore,
        },
    ],
});

export interface FilterState {
    programId?: string;
    assessmentRunId?: string;
}
export const calculateBMI = (weightKg: number, heightMeters: number): number => {
    if (heightMeters <= 0) {
        return 0;
    }
    const bmi = weightKg / (heightMeters * heightMeters);
    return bmi;
};
export const getShortMetricLabel = (t: TFunction, label: string): string => {
    switch (label) {
        case MetricLabels.RunTime:
            return t('traineeReports:performance.ippt.runTimeShort');
        default:
            return label;
    }
};
export const mapTraineeReportData = (
    t: TFunction,
    formatDate: (date: string | Date | Dayjs) => string,
    result: TraineeReportDataFragment
): TraineeReportModel => {
    const { items, teamResilienceItems } = result;
    const profile = items.find(item => item.user?.__typename === 'TenantUser')?.user as
        | TenantUserReportDataFragment
        | undefined;
    const program = items.find(
        item =>
            item.assessmentRun?.__typename === 'AssessmentRun' && item.assessmentRun?.program?.__typename === 'Program'
    )?.assessmentRun?.program as Program | undefined;
    const { result: weight } = items.find(item => item.result.__typename === 'WeightResult') || {};
    const weightResult = weight as WeightAssessmentResultDataFragment | undefined;

    // Performance section
    const {
        result: ippt,
        completedAt: ipptCompletedAt,
        moduleInstance: ipptModuleInstance,
    } = items.find(item => item.result.__typename === 'IpptResult') || {};
    const ipptResult = ippt as IpptAssessmentResultDataFragment | undefined;

    const {
        result: soc,
        completedAt: socCompletedAt,
        moduleInstance: socModuleInstance,
    } = items.find(item => item.result.__typename === 'SocResult') || {};
    const socResult = soc as SocAssessmentResultDataFragment | undefined;

    const {
        result: march20km,
        completedAt: march20kmCompletedAt,
        moduleInstance: march20kmModuleInstance,
    } = items.find(item => item.result.__typename === 'March20kmResult') || {};
    const march20kmResult = march20km as March20kmAssessmentResultDataFragment | undefined;

    const {
        result: finSwim,
        completedAt: finSwimCompletedAt,
        moduleInstance: finSwimModuleInstance,
    } = items.find(item => item.result.__typename === 'FinSwimResult') || {};
    const finSwimResult = finSwim as FinSwimAssessmentResultDataFragment | undefined;

    const {
        result: bmp,
        completedAt: bmpCompletedAt,
        moduleInstance: bmpModuleInstance,
    } = items.find(item => item.result.__typename === 'BmpResult') || {};
    const bmpResult = bmp as BmpAssessmentResultDataFragment | undefined;

    // Trainer Notes section
    const { result: trainerNotes } = items.find(item => item.result.__typename === 'TrainersNotesResult') || {};
    const trainerNotesResult = trainerNotes as TrainersNotesAssessmentResultDataFragment | undefined;

    // Physiological Fitness section
    const { result: strength, moduleInstance: strengthModuleInstance } =
        items.find(item => item.result.__typename === 'StrengthResult') || {};
    const strengthResult = strength as StrengthAssessmentResultDataFragment | undefined;

    const { result: cardioResp, moduleInstance: cardioRespModuleInstance } =
        items.find(item => item.result.__typename === 'CardioRespResult') || {};
    const cardioRespResult = cardioResp as CardioRespAssessmentResultDataFragment | undefined;

    // Psychological Fitness section
    // Self Determination
    const { result: selfDetermination, moduleInstance: selfDeterminationModuleInstance } =
        items.find(item => item.result.__typename === 'SelfDeterminationResult') || {};
    const selfDeterminationResult = selfDetermination as SelfDeterminationAssessmentResultDataFragment | undefined;

    // Use default data if no result exists OR result has empty data (placeholder from backend)
    // Check if result has actual data (not just placeholder with 0 scores)
    const hasActualSelfDeterminationData =
        selfDeterminationResult && selfDeterminationResult.categoryAverages?.some(c => c.averageScore > 0);

    const effectiveSelfDeterminationResult = hasActualSelfDeterminationData
        ? selfDeterminationResult
        : selfDeterminationModuleInstance
          ? createDefaultSelfDeterminationData(t)
          : null;

    const selfDeterminationCategories =
        effectiveSelfDeterminationResult?.categoryAverages.filter(c => c.category !== 'Overall') || [];
    const selfDeterminationRadarData = selfDeterminationCategories.map(c => ({
        axis: c.category,
        individual: Number(c.averageScore.toFixed(2)),
    }));
    const selfDeterminationSummaryCards = [
        ...(effectiveSelfDeterminationResult?.categoryAverages || []).map(c => ({
            label: c.category,
            value:
                c.averageScore === 0
                    ? t('traineeReports:na')
                    : `${Number(c.averageScore).toFixed(1)}/${c.maximumPossibleScore}`,
        })),
    ];

    // Team Resilience
    const { result: teamResilience, moduleInstance: teamResilienceModuleInstance } =
        items.find(item => item.result.__typename === 'TeamResilienceResult') || {};
    const teamResilienceResult = teamResilience as TeamResilienceAssessmentResultDataFragment | undefined;

    // Use default data if no result exists OR result has empty data (placeholder from backend)
    // Check if result has actual data (not just placeholder with 0 scores)
    const hasActualTeamResilienceData =
        teamResilienceResult && teamResilienceResult.subcategoryAverages?.some(s => s.averageScore > 0);

    const effectiveTeamResilienceResult = hasActualTeamResilienceData
        ? teamResilienceResult
        : teamResilienceModuleInstance
          ? createDefaultTeamResilienceData(t)
          : null;

    const individualResources =
        effectiveTeamResilienceResult?.subcategoryAverages.filter(
            s => s.category === DefaultTeamResilienceQuestionCategory.TeamResources
        ) || [];
    const individualRadarData = individualResources.map(s => ({
        axis: s.subcategory,
        individual: Number(s.averageScore.toFixed(2)),
    }));
    const individualResilienceSummaryCards = [
        ...individualResources.map(subcategory => ({
            label: subcategory.subcategory,
            value:
                subcategory.averageScore === 0
                    ? t('traineeReports:na')
                    : `${Number(subcategory.averageScore).toFixed(1)}/${subcategory.maximumPossibleScore}`,
        })),
    ];
    const teamAgg = (teamResilienceItems || []).reduce<Record<string, { total: number; count: number }>>(
        (acc, item) => {
            if (item.result.__typename !== 'TeamResilienceResult') {
                return acc;
            }
            const tr = item.result as TeamResilienceAssessmentResultDataFragment;
            tr.subcategoryAverages
                .filter(sa => sa.category === DefaultTeamResilienceQuestionCategory.TeamResources)
                .forEach(sa => {
                    const key = sa.subcategory;
                    if (!acc[key]) {
                        acc[key] = { total: 0, count: 0 };
                    }
                    acc[key].total += sa.averageScore;
                    acc[key].count += 1;
                });
            return acc;
        },
        {}
    );

    const teamRadarData = individualResources.map(s => {
        const agg = teamAgg[s.subcategory];
        const avg = agg ? Number((agg.total / agg.count).toFixed(2)) : 0;
        return {
            axis: s.subcategory,
            team: avg,
        };
    });

    // Mindful Leadership (ML360: self, buddy, trainer)
    const { result: mlSelf, moduleInstance: mlSelfModuleInstance } =
        items.find(
            item =>
                item.result.__typename === 'ML360Result' &&
                item.moduleInstance?.moduleDefinitionIdentifier === 'ml360-self'
        ) || {};
    const { result: mlBuddy, moduleInstance: mlBuddyModuleInstance } =
        items.find(
            item =>
                item.result.__typename === 'ML360Result' &&
                item.moduleInstance?.moduleDefinitionIdentifier === 'ml360-buddy'
        ) || {};
    const { result: mlTrainer, moduleInstance: mlTrainerModuleInstance } =
        items.find(
            item =>
                item.result.__typename === 'ML360Result' &&
                item.moduleInstance?.moduleDefinitionIdentifier === 'ml360-trainer'
        ) || {};
    const mlSelfResult = mlSelf as Ml360AssessmentResultDataFragment | undefined;
    const mlBuddyResult = mlBuddy as Ml360AssessmentResultDataFragment | undefined;
    const mlTrainerResult = mlTrainer as Ml360AssessmentResultDataFragment | undefined;

    // Use default data if no result exists OR result has empty data (placeholder from backend)
    // Check if result has actual data (not just placeholder with 0 scores)
    const hasActualMlSelfData = mlSelfResult && mlSelfResult.categoryAverages?.some(c => c.averageScore > 0);
    const hasActualMlBuddyData = mlBuddyResult && mlBuddyResult.categoryAverages?.some(c => c.averageScore > 0);
    const hasActualMlTrainerData = mlTrainerResult && mlTrainerResult.categoryAverages?.some(c => c.averageScore > 0);

    const effectiveMlSelfResult = hasActualMlSelfData
        ? mlSelfResult
        : mlSelfModuleInstance
          ? createDefaultMl360Data(t)
          : null;
    const effectiveMlBuddyResult = hasActualMlBuddyData
        ? mlBuddyResult
        : mlBuddyModuleInstance
          ? createDefaultMl360Data(t)
          : null;
    const effectiveMlTrainerResult = hasActualMlTrainerData
        ? mlTrainerResult
        : mlTrainerModuleInstance
          ? createDefaultMl360Data(t)
          : null;

    // Summary cards: show Self category averages (5 items)
    const mlSelfSummary: SimpleMetric[] =
        effectiveMlSelfResult?.categoryAverages?.map(c => ({
            category: c.category,
            score: c.averageScore.toFixed(1),
            label: convertShortLabel(c.category),
            value:
                c.averageScore === 0
                    ? t('traineeReports:na')
                    : `${c.averageScore.toFixed(1)}/${'maxScore' in c ? c.maxScore : c.maximumPossibleScore}`,
        })) || [];

    // Build unified category list from any of the three result types (use defaults if no real data)
    const mlCategories = Array.from(
        new Set<string>([
            ...(effectiveMlSelfResult?.categoryAverages?.map(c => c.category) || []),
            ...(effectiveMlBuddyResult?.categoryAverages?.map(c => c.category) || []),
            ...(effectiveMlTrainerResult?.categoryAverages?.map(c => c.category) || []),
        ])
    );

    const mlSelfBuddyTrainerSummary: RadarDataLeadership[] = mlCategories.map(category => {
        const selfScore =
            effectiveMlSelfResult?.categoryAverages?.find(c => c.category === category)?.averageScore ?? 0;
        const buddyScore =
            effectiveMlBuddyResult?.categoryAverages?.find(c => c.category === category)?.averageScore ?? 0;
        const trainerScore =
            effectiveMlTrainerResult?.categoryAverages?.find(c => c.category === category)?.averageScore ?? 0;
        return {
            axis: convertShortLabel(category),
            self: Number(selfScore.toFixed(2)),
            buddy: Number(buddyScore.toFixed(2)),
            trainer: Number(trainerScore.toFixed(2)),
        };
    });

    const hasAnyMl360 =
        mlSelfModuleInstance ||
        mlBuddyModuleInstance ||
        mlTrainerModuleInstance ||
        (mlSelfResult && mlSelfResult.surveys?.length) ||
        (mlBuddyResult && mlBuddyResult.surveys?.length) ||
        (mlTrainerResult && mlTrainerResult.surveys?.length);
    const ml360ModuleInstanceForDisplay = mlSelfModuleInstance || mlBuddyModuleInstance || mlTrainerModuleInstance;

    // Cognitive Fitness section
    const { result: sart, moduleInstance: sartModuleInstance } =
        items.find(item => item.result.__typename === 'SartResult') || {};
    const sartResult = sart as SartAssessmentResultDataFragment | undefined;
    const sartScore = sartResult?.correctHits
        ? (sartResult.correctHits / (sartResult.correctHits + (sartResult.incorrectPresses || 0))) * 100
        : undefined;

    const { result: visualReaction, moduleInstance: visualReactionModuleInstance } =
        items.find(item => item.result.__typename === 'VisualReactionResult') || {};
    const visualReactionResult = visualReaction as VisualReactionAssessmentResultDataFragment | undefined;
    const visualReactionScore = visualReactionResult?.accuracy;

    const { result: spatialPlanning, moduleInstance: spatialPlanningModuleInstance } =
        items.find(item => item.result.__typename === 'SpatialPlanningResult') || {};
    const spatialPlanningResult = spatialPlanning as SpatialPlanningAssessmentResultDataFragment | undefined;
    const spatialPlanningScore = spatialPlanningResult?.correctAnswers
        ? (spatialPlanningResult.correctAnswers /
              (spatialPlanningResult.correctAnswers + (spatialPlanningResult.incorrectAnswers || 0))) *
          100
        : undefined;

    // Build Performance array only when module names exist
    const performance: Performance[] = [];

    if (ipptModuleInstance?.name) {
        performance.push({
            name: ipptModuleInstance.name,
            date: ipptCompletedAt ? formatDate(ipptCompletedAt) : t('traineeReports:na'),
            result: [
                {
                    label: t('traineeReports:performance.ippt.points'),
                    value: ipptResult?.points ?? t('traineeReports:na'),
                },
                {
                    label: t('traineeReports:performance.ippt.pushUps'),
                    value: ipptResult?.pushUps ?? t('traineeReports:na'),
                },
                {
                    label: t('traineeReports:performance.ippt.sitUps'),
                    value: ipptResult?.sitUps ?? t('traineeReports:na'),
                },
                {
                    label: t('traineeReports:performance.ippt.pullUps'),
                    value: ipptResult?.pullUps ?? t('traineeReports:na'),
                },
                {
                    label: t('traineeReports:performance.ippt.runTime'),
                    value: ipptResult?.runTimeSec
                        ? (formatSecondsAsMinSec(ipptResult.runTimeSec) ?? t('traineeReports:na'))
                        : t('traineeReports:na'),
                },
            ],
        } as Performance);
    }
    if (socModuleInstance?.name) {
        performance.push({
            name: socModuleInstance.name,
            date: socCompletedAt ? formatDate(socCompletedAt) : t('traineeReports:na'),
            result: [
                {
                    label: t('traineeReports:performance.soc.status'),
                    value: socResult?.result ?? t('traineeReports:na'),
                },
                {
                    label: t('traineeReports:performance.soc.completion'),
                    value: socResult?.timingSec
                        ? (formatSecondsAsMinSec(socResult.timingSec) ?? t('traineeReports:na'))
                        : t('traineeReports:na'),
                },
            ],
        } as Performance);
    }
    if (march20kmModuleInstance?.name) {
        performance.push({
            name: march20kmModuleInstance.name,
            date: march20kmCompletedAt ? formatDate(march20kmCompletedAt) : t('traineeReports:na'),
            result: [
                {
                    label: t('traineeReports:performance.march20km.status'),
                    value: march20kmResult?.result ?? t('traineeReports:na'),
                },
                {
                    label: t('traineeReports:performance.march20km.completion'),
                    value: march20kmResult?.timingSec
                        ? (formatSecondsAsHoursMin(march20kmResult.timingSec) ?? t('traineeReports:na'))
                        : t('traineeReports:na'),
                },
            ],
        } as Performance);
    }
    // Check if we have FIN Swim or BMP modules
    const hasFinSwim = finSwimModuleInstance?.moduleDefinitionIdentifier === 'fin-swim';
    const hasBmp = bmpModuleInstance?.moduleDefinitionIdentifier === 'bmp';

    if (hasFinSwim || hasBmp) {
        // Use the latest completion date between FIN Swim and BMP
        const latestCompletedAt =
            finSwimCompletedAt && bmpCompletedAt
                ? new Date(finSwimCompletedAt) > new Date(bmpCompletedAt)
                    ? finSwimCompletedAt
                    : bmpCompletedAt
                : finSwimCompletedAt || bmpCompletedAt;

        const othersResult: SimpleMetric[] = [];

        if (hasFinSwim) {
            othersResult.push({
                label: t('traineeReports:performance.finSwim.timing'),
                value: finSwimResult?.timingSec
                    ? (formatSecondsAsMinSec(finSwimResult.timingSec) ?? t('traineeReports:na'))
                    : t('traineeReports:na'),
            });
        }

        if (hasBmp) {
            othersResult.push({
                label: t('traineeReports:performance.bmp.percentage'),
                value: bmpResult?.percentage ? Number(bmpResult.percentage).toFixed(2) : t('traineeReports:na'),
            });
        }

        performance.push({
            moduleDefinitionIdentifier: 'others',
            name: t('traineeReports:performance.others.title'),
            description: t('traineeReports:performance.others.description'),
            date: latestCompletedAt ? formatDate(latestCompletedAt) : t('traineeReports:na'),
            result: othersResult,
        } as Performance);
    }

    // Physiological Fitness (conditional)
    const physiologicalResults: PhysiologicalResult[] = [];
    if (strengthModuleInstance?.name) {
        physiologicalResults.push({
            moduleDefinitionIdentifier: strengthModuleInstance.moduleDefinitionIdentifier ?? '',
            name: strengthModuleInstance.name ?? '',
            description: strengthModuleInstance.description ?? '',
            result: [
                {
                    label: t('traineeReports:performance.physiologicalFitness.strength.squatRepMaxKg'),
                    value: strengthResult?.squatRepMaxKg
                        ? Number(strengthResult.squatRepMaxKg).toFixed(2)
                        : t('traineeReports:na'),
                },
                {
                    label: t('traineeReports:performance.physiologicalFitness.strength.deadliftRepMaxKg'),
                    value: strengthResult?.deadliftRepMaxKg
                        ? Number(strengthResult.deadliftRepMaxKg).toFixed(2)
                        : t('traineeReports:na'),
                },
                {
                    label: t('traineeReports:performance.physiologicalFitness.strength.maxPullUps'),
                    value: strengthResult?.maxPullUps ?? t('traineeReports:na'),
                },
                {
                    label: t('traineeReports:performance.physiologicalFitness.strength.sprintDragCarrySec'),
                    value: strengthResult?.sprintDragCarrySec
                        ? (formatSecondsAsMinSec(strengthResult.sprintDragCarrySec) ?? t('traineeReports:na'))
                        : t('traineeReports:na'),
                },
            ] as SimpleMetric[],
        } as PhysiologicalResult);
    }
    if (cardioRespModuleInstance?.name) {
        physiologicalResults.push({
            moduleDefinitionIdentifier: cardioRespModuleInstance.moduleDefinitionIdentifier ?? '',
            name: cardioRespModuleInstance.name ?? '',
            description: cardioRespModuleInstance.description ?? '',
            result: [
                {
                    label: t('traineeReports:performance.physiologicalFitness.cardioResp.vo2Max'),
                    value: cardioRespResult?.vo2Max
                        ? Number(cardioRespResult.vo2Max).toFixed(2)
                        : t('traineeReports:na'),
                },
                {
                    label: t('traineeReports:performance.physiologicalFitness.cardioResp.restingHR'),
                    value: cardioRespResult?.restingHR ?? t('traineeReports:na'),
                },
                {
                    label: t('traineeReports:performance.physiologicalFitness.cardioResp.exerciseHR'),
                    value: cardioRespResult?.exerciseHR ?? t('traineeReports:na'),
                },
                {
                    label: t('traineeReports:performance.physiologicalFitness.cardioResp.hrvMs'),
                    value: cardioRespResult?.hrvMs ?? t('traineeReports:na'),
                },
            ] as SimpleMetric[],
        } as PhysiologicalResult);
    }

    // Psychological Fitness (conditional)
    const psychologicalResults: PsychologicalResult[] = [];
    if (selfDeterminationModuleInstance?.moduleDefinitionIdentifier) {
        psychologicalResults.push({
            moduleDefinitionIdentifier: selfDeterminationModuleInstance.moduleDefinitionIdentifier ?? '',
            name: selfDeterminationModuleInstance.name ?? '',
            description: selfDeterminationModuleInstance.description ?? '',
            result: selfDeterminationSummaryCards,
            charts: [
                {
                    data: selfDeterminationRadarData,
                    footer: t('traineeReports:labels.individual'),
                    maxDomain: 5,
                },
            ] as ChartData[],
        } as PsychologicalResult);
    }
    if (teamResilienceModuleInstance?.moduleDefinitionIdentifier) {
        psychologicalResults.push({
            moduleDefinitionIdentifier: teamResilienceModuleInstance.moduleDefinitionIdentifier ?? '',
            name: teamResilienceModuleInstance.name ?? '',
            description: teamResilienceModuleInstance.description ?? '',
            result: individualResilienceSummaryCards as SimpleMetric[],
            charts: [
                {
                    data: individualRadarData as RadarDatum[],
                    footer: t('traineeReports:labels.individual'),
                    maxDomain: 100,
                },
                {
                    data: teamRadarData as RadarDatum[],
                    footer: t('traineeReports:labels.team'),
                    maxDomain: 100,
                },
            ] as ChartData[],
        } as PsychologicalResult);
    }
    if (hasAnyMl360) {
        let maxDomain = DefaultScores.Ml360MaxScore;

        if (effectiveMlSelfResult?.categoryAverages) {
            for (const category of effectiveMlSelfResult.categoryAverages) {
                let maxScore = 0;
                if ('maxScore' in category) {
                    maxScore = category.maxScore;
                } else if ('maximumPossibleScore' in category) {
                    maxScore = category.maximumPossibleScore;
                }

                if (maxScore > maxDomain) {
                    maxDomain = maxScore;
                }
            }
        }

        psychologicalResults.push({
            moduleDefinitionIdentifier: ml360ModuleInstanceForDisplay?.moduleDefinitionIdentifier ?? 'ml360-self',
            name: t('traineeReports:ml360.title'),
            description: t('traineeReports:ml360.description'),
            result: mlSelfSummary,
            charts: [
                {
                    data: mlSelfBuddyTrainerSummary,
                    series: [
                        {
                            key: 'self',
                            name: t('traineeReports:labels.self'),
                            stroke: '#2563EB',
                            fill: '#3B82F6',
                            fillOpacity: 0.3,
                        } as ChartSeries,
                        {
                            key: 'buddy',
                            name: t('traineeReports:labels.buddy'),
                            stroke: '#059669',
                            fill: '#10B981',
                            fillOpacity: 0.22,
                        } as ChartSeries,
                        {
                            key: 'trainer',
                            name: t('traineeReports:labels.trainer'),
                            stroke: '#DC2626',
                            fill: '#F87171',
                            fillOpacity: 0.18,
                        } as ChartSeries,
                    ] as ChartSeries[],
                    maxDomain: maxDomain,
                    height: 280,
                    footer: t('traineeReports:labels.individual'),
                },
            ] as ChartData[],
        } as PsychologicalResult);
    }

    // Cognitive Fitness (conditional)
    const cognitiveFitness: CognitiveFitness[] = [];
    if (sartModuleInstance?.name) {
        cognitiveFitness.push({
            name: sartModuleInstance.name ?? '',
            moduleDefinitionIdentifier: sartModuleInstance.moduleDefinitionIdentifier ?? '',
            description: sartModuleInstance.description ?? '',
            result: [
                {
                    label: t('traineeReports:performance.cognitiveFitness.scoreLabel'),
                    value: sartScore
                        ? t('traineeReports:performance.cognitiveFitness.scoreValue', {
                              value: sartScore.toFixed(2),
                          })
                        : t('traineeReports:na'),
                },
                {
                    label: t('traineeReports:performance.cognitiveFitness.avgResponseTimeMs'),
                    value: sartResult?.averageResponseTimeMs
                        ? `${Math.round(sartResult.averageResponseTimeMs)}`
                        : t('traineeReports:na'),
                },
            ] as SimpleMetric[],
        } as CognitiveFitness);
    }
    if (visualReactionModuleInstance?.name) {
        cognitiveFitness.push({
            name: visualReactionModuleInstance.name ?? '',
            moduleDefinitionIdentifier: visualReactionModuleInstance.moduleDefinitionIdentifier ?? '',
            description: visualReactionModuleInstance.description ?? '',
            result: [
                {
                    label: t('traineeReports:performance.cognitiveFitness.scoreLabel'),
                    value: visualReactionScore
                        ? t('traineeReports:performance.cognitiveFitness.scoreValue', {
                              value: visualReactionScore.toFixed(2),
                          })
                        : t('traineeReports:na'),
                },
                {
                    label: t('traineeReports:performance.cognitiveFitness.avgResponseTimeMs'),
                    value: visualReactionResult?.averageResponseTimeMs
                        ? `${Math.round(visualReactionResult.averageResponseTimeMs)}`
                        : t('traineeReports:na'),
                },
            ] as SimpleMetric[],
        } as CognitiveFitness);
    }
    if (spatialPlanningModuleInstance?.name) {
        cognitiveFitness.push({
            name: spatialPlanningModuleInstance.name ?? '',
            moduleDefinitionIdentifier: spatialPlanningModuleInstance.moduleDefinitionIdentifier ?? '',
            description: spatialPlanningModuleInstance.description ?? '',
            result: [
                {
                    label: t('traineeReports:performance.cognitiveFitness.scoreLabel'),
                    value: spatialPlanningScore
                        ? t('traineeReports:performance.cognitiveFitness.scoreValue', {
                              value: spatialPlanningScore.toFixed(2),
                          })
                        : t('traineeReports:na'),
                },
                {
                    label: t('traineeReports:performance.cognitiveFitness.avgResponseTimeMs'),
                    value: spatialPlanningResult?.averageResponseTimeMs
                        ? `${Math.round(spatialPlanningResult.averageResponseTimeMs)}`
                        : t('traineeReports:na'),
                },
            ] as SimpleMetric[],
        } as CognitiveFitness);
    }

    const report: TraineeReportModel = {
        profile: {
            name: profile ? `${profile.firstName || ''} ${profile.lastName || ''}` : '',
            dob: profile && profile.dateOfBirth ? formatDate(profile.dateOfBirth) : t('traineeReports:na'),
            gender:
                profile && profile.gender
                    ? t('traineeReports:profile.gender.' + profile.gender)
                    : t('traineeReports:na'),
            heightCm:
                profile && profile.height
                    ? t('traineeReports:profile.heightCm', { value: profile.height.toFixed(2) })
                    : t('traineeReports:na'),
            weightKg: weightResult?.weightKg
                ? t('traineeReports:profile.weightKg', { value: weightResult.weightKg.toFixed(2) })
                : t('traineeReports:na'),
            bmi:
                profile && profile.height && weightResult?.weightKg
                    ? calculateBMI(weightResult.weightKg, profile.height / 100).toFixed(2)
                    : t('traineeReports:na'),
            program: program?.name || t('traineeReports:na'),
            completedAt: (() => {
                const completedItems = (items || []).filter(
                    i => !!i.completedAt && !!i.assessmentRun && i.assessmentRun.__typename === 'AssessmentRun'
                );
                if (completedItems.length === 0) {
                    return t('traineeReports:na');
                }
                const latest = completedItems
                    .map(i => i.completedAt as string)
                    .reduce<string>(
                        (acc, curr) => (new Date(curr) > new Date(acc) ? curr : acc),
                        completedItems[0].completedAt as string
                    );
                return formatDate(latest);
            })(),
        } as Profile,
        performance,
        trainerNotes: trainerNotesResult?.trainersNotes || t('traineeReports:na'),
        physiologicalFitness: {
            result: physiologicalResults,
        } as PhysiologicalFitness,
        psychologicalFitness: {
            results: psychologicalResults,
        },
        cognitiveFitness,
    };

    return report;
};
